import { getTenantUnits } from '@core/configuration';
import Enums from '@core/types/api/Enums';
import type { Units } from '@core/types/units';

import { getDefaultUnitForCategory } from './forecastDistribution';
import { getShortUnitLabel, getUnitLabel } from './unitHelpers';

/**
 * Product categories measured in tenant default weight units show the same raw
 * UOM; suffix disambiguates (legacy parity with T(agg) / T(asph)).
 */
const WEIGHT_VOLUME_CATEGORY_SUFFIXES: Partial<Record<Enums.ProductCategory, string>> = {
  [Enums.ProductCategory.Aggregate]: ' (aggregate)',
  [Enums.ProductCategory.Asphalt]: ' (asphalt)',
};

/**
 * Tenant-aware quantity unit label for Actuals backlog / dashboard metrics:
 * canonical unit display string plus a parenthetical product category when needed.
 */
export const getActualsQuantityUnitDisplay = (
  category: Enums.ProductCategory,
  tenantUnits: Units = getTenantUnits(),
): string => {
  const unitEnum = getDefaultUnitForCategory(category, tenantUnits);
  const label = getUnitLabel(unitEnum);
  const base = label !== '' ? label : String(unitEnum);
  const suffix = WEIGHT_VOLUME_CATEGORY_SUFFIXES[category] ?? '';
  return `${base}${suffix}`;
};

/**
 * Compact variant of {@link getActualsQuantityUnitDisplay} for spreadsheet headers,
 * e.g. "CY" instead of "Cubic Yard".
 */
export const getActualsQuantityShortUnitDisplay = (
  category: Enums.ProductCategory,
  tenantUnits: Units = getTenantUnits(),
): string => {
  const unitEnum = getDefaultUnitForCategory(category, tenantUnits);
  const label = getShortUnitLabel(unitEnum);
  const suffix = WEIGHT_VOLUME_CATEGORY_SUFFIXES[category] ?? '';
  return `${label}${suffix}`;
};
